/**
 * Cart Page Related Products Carousel
 * Optimized for performance with lazy loading and intersection observer
 */

(function() {
  'use strict';

  // Early exit if required elements don't exist
  const relatedProductsContainer = document.getElementById('related-products-container');
  const dotsContainer = document.getElementById('carousel-dots');
  
  if (!relatedProductsContainer || !dotsContainer) {
    console.warn('Carousel containers not found');
    return;
  }

  // Configuration
  const CONFIG = {
    maxProducts: 12,
    autoplayInterval: 3000,
    autoplayDelay: 5000, // Wait 5s before starting autoplay (better for LCP)
    observerThreshold: 0.1,
    itemsPerSlide: window.innerWidth < 768 ? 1 : 4
  };

  // State
  let currentSlide = 0;
  let slideInterval = null;
  let carouselObserver = null;

  /**
   * Build product data array from DOM elements
   */
  function collectProducts(cartItemCount, cartProductTitles, cartTags) {
    const products = [];
    
    if (cartItemCount === 0) {
      const productDivs = document.querySelectorAll('#best-selling-products .product-data');
      
      if (productDivs.length === 0) {
        console.error("No products found in 'best-selling-collection'");
        return products;
      }
      
      productDivs.forEach(productDiv => {
        const title = productDiv.dataset.title.toLowerCase();
        if (!cartProductTitles.includes(title)) {
          products.push(extractProductData(productDiv));
        }
      });
    } else {
      const productDivs = document.querySelectorAll('#all-products .product-data');
      const excludedTags = ["Popular picks", "Bestselling Products", "All Product", "Issviva Products", "Discount product", "Subscription"];
      
      if (productDivs.length === 0) {
        console.error("No products found in '#all-products'");
        return products;
      }
      
      productDivs.forEach(productDiv => {
        const productTags = filterTags(productDiv.dataset.tags.split(','), excludedTags);
        const title = productDiv.dataset.title.toLowerCase();
        
        if (!cartProductTitles.includes(title) && hasMatchingTag(productTags, cartTags)) {
          products.push(extractProductData(productDiv));
        }
      });
    }
    
    return products.slice(0, CONFIG.maxProducts);
  }

  /**
   * Extract product data from DOM element
   */
  function extractProductData(productDiv) {
    return {
      title: productDiv.dataset.title,
      url: productDiv.dataset.url,
      price: productDiv.dataset.price,
      imageSmall: productDiv.dataset.imageSmall,
      imageMedium: productDiv.dataset.imageMedium,
      imageLarge: productDiv.dataset.imageLarge,
      variantId: productDiv.dataset.variantId,
      hasStock: productDiv.dataset.stock === 'true'
    };
  }

  /**
   * Filter out excluded tags
   */
  function filterTags(tags, excludedTags) {
    return tags.filter(tag => !excludedTags.includes(tag));
  }

  /**
   * Check if product has matching tag
   */
  function hasMatchingTag(productTags, cartTags) {
    return productTags.some(tag => cartTags.includes(tag));
  }

  /**
   * Build HTML for product card
   * All images in first slide get high priority to prevent LCP issues
   */
  function buildProductHTML(product, isFirstSlide) {
    // All first slide images load eagerly with high priority
    // This prevents LCP detection issues when carousel transitions
    const loadingAttr = isFirstSlide ? 'eager' : 'lazy';
    const fetchPriorityAttr = isFirstSlide ? 'high' : 'auto';
    
    const stockButton = product.hasStock
      ? `<form method="post" action="/cart/add" class="add-to-cart-form">
           <input name="id" value="${product.variantId}" type="hidden">
           <div class="btn-add-to-cart-cart">
             <button type="submit" class="btn">ADD TO CART</button>
           </div>
         </form>`
      : `<div class="add-to-cart-form">
           <input name="id" value="${product.variantId}" type="hidden">
           <div class="btn-add-to-cart-cart">
             <button type="button" disabled class="btn sold-out-btn">SOLD OUT</button>
           </div>
         </div>`;
    
    return `
      <div class="related-product">
        <a href="${product.url}">
          <picture class="cart-picture-element">
            <source 
              media="(max-width: 767px)" 
              srcset="${product.imageSmall} 400w"
              sizes="100vw">
            <source 
              media="(min-width: 768px) and (max-width: 1023px)" 
              srcset="${product.imageMedium} 600w"
              sizes="50vw">
            <source 
              media="(min-width: 1024px)" 
              srcset="${product.imageLarge} 800w"
              sizes="25vw">
            <img 
              src="${product.imageMedium}" 
              alt="${product.title}"
              width="400"
              height="400"
              loading="${loadingAttr}"
              fetchpriority="${fetchPriorityAttr}"
              decoding="async"
              class="related-product-image">
          </picture>
          <div class="related-products-title-container">
            <p>${product.title}</p>
          </div>
          <div class="grid-product__price">${product.price}</div>
        </a>
        ${stockButton}
      </div>
    `;
  }

  /**
   * Build carousel HTML
   */
  function buildCarouselHTML(products, skipFirstSlide = false) {
    let slidesHTML = '';
    let dotsHTML = '';
    let slideIndex = 0;
    
    // Calculate starting point
    const startIndex = skipFirstSlide ? CONFIG.itemsPerSlide : 0;
    
    // Add dot for server-rendered first slide if it exists
    if (skipFirstSlide) {
      dotsHTML += `<span class="dot active" data-slide="0"></span>`;
      slideIndex = 1;
    }
    
    for (let i = startIndex; i < products.length; i += CONFIG.itemsPerSlide) {
      const slideProducts = products.slice(i, i + CONFIG.itemsPerSlide);
      const isFirstSlide = i === 0 && !skipFirstSlide;
      const activeClass = isFirstSlide ? ' active' : '';
      
      let slideHTML = `<div class="carousel-slide${activeClass}">`;
      slideProducts.forEach(product => {
        slideHTML += buildProductHTML(product, isFirstSlide);
      });
      slideHTML += '</div>';
      
      slidesHTML += slideHTML;
      dotsHTML += `<span class="dot${activeClass}" data-slide="${slideIndex}"></span>`;
      slideIndex++;
    }
    
    return { slidesHTML, dotsHTML };
  }

  /**
   * Navigate to specific slide
   */
  function goToSlide(n, slides, dots) {
    if (!slides.length || !dots.length) return;
    
    slides[currentSlide].classList.remove('active');
    dots[currentSlide].classList.remove('active');
    
    currentSlide = (n + slides.length) % slides.length;
    
    slides[currentSlide].classList.add('active');
    dots[currentSlide].classList.add('active');
    
    relatedProductsContainer.style.transform = `translateX(-${currentSlide * 100}%)`;
  }

  /**
   * Move to next slide
   */
  function nextSlide(slides, dots) {
    goToSlide(currentSlide + 1, slides, dots);
  }

  /**
   * Start autoplay
   */
  function startAutoplay(slides, dots) {
    stopAutoplay(); // Clear any existing interval
    slideInterval = setInterval(() => nextSlide(slides, dots), CONFIG.autoplayInterval);
  }

  /**
   * Stop autoplay
   */
  function stopAutoplay() {
    if (slideInterval) {
      clearInterval(slideInterval);
      slideInterval = null;
    }
  }

  /**
   * Setup intersection observer for autoplay optimization
   */
  function setupIntersectionObserver(slides, dots) {
    if (!('IntersectionObserver' in window)) {
      // Fallback: start autoplay with delay
      setTimeout(() => startAutoplay(slides, dots), CONFIG.autoplayDelay);
      return;
    }
    
    carouselObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Delay autoplay start to improve LCP
          setTimeout(() => startAutoplay(slides, dots), CONFIG.autoplayDelay);
        } else {
          stopAutoplay();
        }
      });
    }, { threshold: CONFIG.observerThreshold });
    
    carouselObserver.observe(relatedProductsContainer);
    
    // Disconnect after first observation to prevent re-triggering
    setTimeout(() => {
      if (carouselObserver) {
        carouselObserver.disconnect();
        // Re-observe only for pause/resume when scrolling
        setupScrollPauseObserver(slides, dots);
      }
    }, CONFIG.autoplayDelay + 1000);
  }

  /**
   * Setup observer to pause carousel when scrolled out of view
   */
  function setupScrollPauseObserver(slides, dots) {
    const scrollObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) {
          stopAutoplay();
        } else if (!slideInterval) {
          startAutoplay(slides, dots);
        }
      });
    }, { threshold: 0.1 });
    
    scrollObserver.observe(relatedProductsContainer);
  }

  /**
   * Setup dot navigation
   */
  function setupDotNavigation(slides, dots) {
    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        stopAutoplay();
        goToSlide(index, slides, dots);
        startAutoplay(slides, dots);
      });
    });
  }

  /**
   * Handle add to cart form submission
   */
  function handleAddToCart(event) {
    event.preventDefault();
    
    const form = event.target;
    const formData = new FormData(form);
    const submitButton = form.querySelector('button[type="submit"]');
    const originalText = submitButton.textContent;
    
    // Visual feedback
    submitButton.textContent = 'ADDING...';
    submitButton.disabled = true;
    
    fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
    })
    .then(response => {
      if (!response.ok) throw new Error('Failed to add to cart');
      return response.json();
    })
    .then(data => {
      console.log('Product added to cart:', data);
      window.location.reload();
    })
    .catch(error => {
      console.error('Error adding product to cart:', error);
      submitButton.textContent = 'ERROR';
      submitButton.disabled = false;
      
      // Reset button after 2 seconds
      setTimeout(() => {
        submitButton.textContent = originalText;
        submitButton.disabled = false;
      }, 2000);
    });
  }

  /**
   * Setup add to cart forms
   */
  function setupAddToCartForms() {
    // Use event delegation to handle both server-rendered and JS-generated forms
    document.addEventListener('submit', function(event) {
      if (event.target.classList.contains('add-to-cart-form')) {
        handleAddToCart(event);
      }
    });
  }

  /**
   * Initialize carousel
   */
  function initCarousel(cartItemCount, cartProductTitles, cartTags) {
    // Check if first slide is already server-rendered
    const serverRenderedSlide = relatedProductsContainer.querySelector('[data-server-rendered="true"]');
    const hasServerRenderedSlide = serverRenderedSlide !== null;
    
    // Collect products
    const products = collectProducts(cartItemCount, cartProductTitles, cartTags);
    
    if (products.length === 0 && !hasServerRenderedSlide) {
      console.warn('No products available for carousel');
      return;
    }
    
    // Build and inject HTML (skip first slide if server-rendered)
    const { slidesHTML, dotsHTML } = buildCarouselHTML(products, hasServerRenderedSlide);
    
    // Append additional slides after server-rendered one
    if (hasServerRenderedSlide) {
      relatedProductsContainer.insertAdjacentHTML('beforeend', slidesHTML);
    } else {
      relatedProductsContainer.innerHTML = slidesHTML;
    }
    
    dotsContainer.innerHTML = dotsHTML;
    
    // Get references to slides and dots
    const slides = document.querySelectorAll('.carousel-slide');
    const dots = document.querySelectorAll('.dot');
    
    if (slides.length === 0 || dots.length === 0) {
      console.error('No slides or dots found after rendering');
      return;
    }
    
    // Setup add to cart forms for all slides (including server-rendered)
    setupAddToCartForms();
    
    // Only setup carousel functionality if there are multiple slides
    if (slides.length > 1) {
      setupIntersectionObserver(slides, dots);
      setupDotNavigation(slides, dots);
    }
  }

  /**
   * Cleanup function
   */
  function cleanup() {
    stopAutoplay();
    if (carouselObserver) {
      carouselObserver.disconnect();
      carouselObserver = null;
    }
  }

  // Expose initialization function to global scope
  window.initCartCarousel = function(cartItemCount, cartProductTitles, cartTags) {
    // Clean up any existing carousel
    cleanup();
    
    // Initialize new carousel
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        initCarousel(cartItemCount, cartProductTitles, cartTags);
      });
    } else {
      initCarousel(cartItemCount, cartProductTitles, cartTags);
    }
  };

  // Cleanup on page unload
  window.addEventListener('beforeunload', cleanup);

})();